import { Loading01Icon } from '@repo/studio-design-system-ui-icons/general/loading-01'
import * as React from 'react'
import { tw } from 'utils/classnames'

import { ClearSpinner } from '@repo/studio-components-spinner/clear-spinner'
import { IconHolder } from '@repo/studio-components-icon-holder'

const loadedSrcCache = new Set<string>()

interface LoadedImageProps extends React.ComponentProps<'div'> {
  src?: string | null
  alt?: string
  ref?: React.Ref<HTMLDivElement>
  loading?: 'lazy' | 'eager'
  loader?: React.ReactNode
  /** Switches between spinner styles. 'clear-spinner' uses the multi-color ClearSpinner. */
  variant?: 'default' | 'clear-spinner'
  /** How the image fits its container. 'cover' fills and crops, 'contain' scales to fit. */
  imageVariant?: 'cover' | 'contain'
  /** Remembers loaded srcs across unmount/remount to avoid spinner flashes.
   * Disable for dynamic src values that shouldn't be cached. */
  cacheLoaded?: boolean
  /** Animate the image when it is hovered */
  animateOnHover?: boolean
  onError?: React.ReactEventHandler<HTMLImageElement>
}

const defaultLoader = (
  <IconHolder className="animate-spin">
    <Loading01Icon />
  </IconHolder>
)

const clearSpinnerLoader = <ClearSpinner />

export function LoadedImage({
  src,
  alt,
  className,
  children,
  ref,
  loader,
  variant = 'default',
  loading = 'lazy',
  imageVariant = 'cover',
  cacheLoaded = false,
  animateOnHover,
  onError,
  ...props
}: LoadedImageProps) {
  const resolvedLoader =
    loader ?? (variant === 'clear-spinner' ? clearSpinnerLoader : defaultLoader)
  const [showLoader, setShowLoader] = React.useState(false)
  const [isLoaded, setIsLoaded] = React.useState(() =>
    Boolean(cacheLoaded && src && loadedSrcCache.has(src)),
  )

  const handleLoad = () => {
    if (cacheLoaded && src) {
      loadedSrcCache.add(src)
    }
    setIsLoaded(true)
  }

  // show the loader for 500ms after the component mounts
  // this prevents the loader from flashing briefly
  // when the image loads quickly
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setShowLoader(true)
    }, 500)
    return () => clearTimeout(timeout)
  }, [isLoaded])

  if (!src) {
    return (
      <div
        ref={ref}
        className={tw(
          'relative flex items-center justify-center overflow-hidden h-full',
          className,
        )}
        {...props}
      >
        {showLoader ? resolvedLoader : null}
        {children}
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={tw('relative flex items-center justify-center overflow-hidden', className)}
      {...props}
    >
      <div className={tw('absolute pointer-events-none', isLoaded ? 'opacity-0' : 'opacity-100')}>
        {showLoader ? resolvedLoader : null}
      </div>
      <img
        src={src}
        alt={alt}
        loading={loading}
        onLoad={handleLoad}
        onError={onError}
        className={tw(
          imageVariant === 'cover' ? 'w-full h-full object-cover' : '',
          imageVariant === 'contain' ? 'w-auto h-auto max-h-full max-w-full object-contain' : '',
          isLoaded ? 'opacity-100' : 'opacity-0',
          animateOnHover
            ? 'transition-[opacity,transform] duration-200 group-hover:scale-105'
            : 'transition-opacity duration-150',
        )}
      />
      {children}
    </div>
  )
}
